import { State } from '@hookstate/core';
import { useEffect, useMemo, useRef } from 'react';
import { useGameType } from '../hooks/use-game-type';
import { useSounds } from '../hooks/use-sound-handler';
import { createTimer } from '../lib/create-timer';
import { getQuery } from '../lib/query';
import {
    FireBallStatus,
    GameState,
    MetaState,
    WinnerState,
} from '../stores/backend';

export const useBackgroundSounds = (
    game: State<GameState>,
    calls: State<number[]>,
    meta: State<MetaState>,
    winners: State<WinnerState>,
    fireBallStatus: State<FireBallStatus>
) => {
    const announcerRef = useRef({
        lastcall: false,
        nosales: false,
        starting: false,
    });
    const partWinnerPlayedRef = useRef(false);
    const previousFireBallStatusRef = useRef<FireBallStatus>('active');

    const { play, initialized } = useSounds();
    const type = useGameType(game);
    const last = calls.value[calls.value.length - 1];
    const hasCards = Object.keys(game.player_cards.value || {}).length > 0;

    const winner = meta.modals.winner.value;
    const partWinnerPanel = meta.panels.partWinner.value;
    const timer = meta.timer.date.value;
    const fireBall = fireBallStatus.value;
    const userId = getQuery().USERID;
    const hasLocalFireBallWin = winners.winners.value.some(
        (winner) => winner.fireBallWin && winner.userId === userId
    );

    const currentPart = game.current_part.value + 1;
    const gameParts = game.game_parts.value;
    const parts = useMemo(() => Object.keys(gameParts).length, [gameParts]);

    useEffect(() => {
        if (
            !initialized ||
            !meta.sound.value ||
            typeof last === 'undefined' ||
            !hasCards
        ) {
            return;
        }

        const id = last.toString().padStart(3, '0');

        if (last === Number(id)) {
            play(`ball${type}`, id);
        }
    }, [initialized, play, last, meta.sound.value, type, hasCards]);

    useEffect(() => {
        if (!winner || !hasCards) {
            return;
        }

        async function playWinner() {
            if (currentPart === parts) {
                const sounds: Promise<unknown>[] = [play('announcer', 'bingo')];

                if (hasLocalFireBallWin) {
                    sounds.push(play('ui', 'magicFlameBurn'));
                }

                await Promise.allSettled(sounds);
                await play('ui', 'winnerbox');
            } else {
                await play('ui', 'partchange');
            }
        }

        playWinner();
    }, [winner, play, currentPart, parts, hasCards, hasLocalFireBallWin]);

    useEffect(() => {
        if (!partWinnerPanel) {
            partWinnerPlayedRef.current = false;
            return;
        }

        if (!partWinnerPlayedRef.current && winners.winners.value?.length) {
            play('ui', 'partwinner');
            partWinnerPlayedRef.current = true;
        }
    }, [partWinnerPanel, winners.winners.value, play]);

    useEffect(() => {
        const previous = previousFireBallStatusRef.current;
        previousFireBallStatusRef.current = fireBall;

        if (
            !initialized ||
            !meta.sound.value ||
            previous !== 'pending' ||
            fireBall !== 'snuffed'
        ) {
            return;
        }

        play('ui', 'smoke');
    }, [fireBall, initialized, meta.sound.value, play]);

    useEffect(() => {
        const { end } = createTimer(timer, (seconds) => {
            if (seconds <= 10 && !announcerRef.current.starting) {
                play('announcer', 'starting');
                announcerRef.current.starting = true;
            } else if (seconds <= 20 && !announcerRef.current.nosales) {
                play('announcer', 'nosales');
                announcerRef.current.nosales = true;
            } else if (seconds <= 30 && !announcerRef.current.lastcall) {
                play('announcer', 'lastcall');
                announcerRef.current.lastcall = true;
            }
        });

        return () => {
            announcerRef.current = {
                starting: false,
                nosales: false,
                lastcall: false,
            };
            end();
        };
    }, [play, timer]);
};
